/**
 * The one place this application talks to the server.
 *
 * The API answers on the **same origin** as these files, which is what lets the
 * session stay an `httpOnly`, `__Host-` prefixed cookie that JavaScript cannot
 * read. A token in `localStorage` would be readable by any successful XSS and
 * could not be revoked before it expired; nothing here ever handles a
 * credential.
 *
 * What it does handle is the CSRF secret, which is deliberately readable: the
 * server sets it in a non-httpOnly cookie and expects it echoed in a header on
 * every mutating request. A cross-site page can cause the cookie to be *sent*
 * but cannot *read* it, which is the whole of the double-submit scheme.
 */

export class ApiError extends Error {
  constructor(
    readonly status: number,
    message: string,
    /** The form field at fault, when the server could name one. */
    readonly field: string | null = null,
    /** The capability a refusal was about, for a 403. */
    readonly need: string | null = null,
  ) {
    super(message);
    this.name = 'ApiError';
  }

  /** True when signing in would plausibly fix this. */
  get isUnauthorized() {
    return this.status === 401;
  }

  /** True when the thing asked for is not here — or is not ours to see. */
  get isNotFound() {
    return this.status === 404;
  }
}

const CSRF_COOKIE = /(?:^|;\s*)(?:__Host-)?co_csrf=([^;]*)/;

/** The CSRF secret the server set, or null when nobody is signed in. */
export function csrfToken(): string | null {
  const match = document.cookie.match(CSRF_COOKIE);
  return match?.[1] ? decodeURIComponent(match[1]) : null;
}

type Method = 'GET' | 'POST' | 'PATCH' | 'DELETE';

interface Options {
  method?: Method;
  body?: unknown;
  /** Abort with the caller's signal, so a superseded request stops costing. */
  signal?: AbortSignal;
}

async function request<T>(path: string, options: Options = {}): Promise<T> {
  const method = options.method ?? 'GET';
  const headers: Record<string, string> = {};

  if (options.body !== undefined) headers['Content-Type'] = 'application/json';

  if (method !== 'GET') {
    const token = csrfToken();
    if (token) headers['X-CSRF-Token'] = token;
  }

  const response = await fetch(path, {
    method,
    headers,
    // Same-origin, so the session cookie rides along without the SPA touching it.
    credentials: 'same-origin',
    body: options.body === undefined ? undefined : JSON.stringify(options.body),
    signal: options.signal,
  });

  if (response.status === 204) return undefined as T;

  const text = await response.text();
  let payload: unknown = null;
  try {
    payload = text ? JSON.parse(text) : null;
  } catch {
    payload = null;
  }

  if (!response.ok) {
    const detail = (payload ?? {}) as { error?: string; field?: string; need?: string };
    throw new ApiError(
      response.status,
      detail.error ?? `The server answered ${response.status}.`,
      detail.field ?? null,
      detail.need ?? null,
    );
  }

  return payload as T;
}

export const api = {
  get: <T>(path: string, signal?: AbortSignal) => request<T>(path, { signal }),
  post: <T>(path: string, body?: unknown, signal?: AbortSignal) =>
    request<T>(path, { method: 'POST', body: body ?? {}, signal }),
  patch: <T>(path: string, body?: unknown, signal?: AbortSignal) =>
    request<T>(path, { method: 'PATCH', body: body ?? {}, signal }),
  delete: <T>(path: string, signal?: AbortSignal) =>
    request<T>(path, { method: 'DELETE', signal }),
};

/**
 * A message worth showing somebody.
 *
 * An `ApiError` already carries wording the server chose for a human; anything
 * else is a network fault, and saying so is more use than an exception's text.
 */
export function describeError(error: unknown): string {
  if (error instanceof ApiError) return error.message;
  if (error instanceof DOMException && error.name === 'AbortError') return '';
  return 'Could not reach the server. Check your connection and try again.';
}
